import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apiUrl } from '../config/api';
const MONTH_NAMES = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

const styles = {
  page: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  toolbar: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' },
  filterLabel: { display: 'block', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: 4 },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' },
  summaryCard: { border: '1px solid #e5e7eb', borderRadius: '12px', padding: '12px 14px', background: '#fff', display: 'flex', flexDirection: 'column', gap: '4px' },
  summaryLabel: { fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 },
  summaryValue: { fontSize: 20, fontWeight: 700, color: '#111827' },
  select: { padding: '6px 10px', borderRadius: '10px', border: '2px solid #94a3b8', background: '#fff', fontSize: 14, boxShadow: '0 2px 6px rgba(15,23,42,0.08)' },
  button: { padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 600 },
  secondaryBtn: { padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500 },
  tableWrap: { border: '1px solid #e5e7eb', borderRadius: '16px', background: '#fff', boxShadow: '0 8px 20px rgba(15,23,42,0.06)' },
  tableScroll: { width: '100%', overflowX: 'auto' },
  table: { width: '100%', minWidth: 1200, borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f7f7fb', textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#4b5563' },
  td: { borderBottom: '1px solid #f1f5f9', padding: '8px 8px', verticalAlign: 'top' },
  input: { width: '100%', border: '2px solid #94a3b8', borderRadius: '10px', padding: '6px 10px', fontSize: 13, background: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.08)' },
  columnFilterInput: { width: '100%', border: '1px solid #cbd5f5', borderRadius: '8px', padding: '4px 8px', fontSize: 12, background: '#fff' },
  columnFilterSelect: { width: '100%', border: '1px solid #cbd5f5', borderRadius: '8px', padding: '4px 6px', fontSize: 12, background: '#fff' },
  textarea: { width: '100%', minHeight: 60, border: '2px solid #94a3b8', borderRadius: '12px', padding: '8px 10px', fontSize: 13, resize: 'vertical', background: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.08)' },
  grid: { display: 'flex', alignItems: 'flex-start', gap: '20px' },
  sidebar: { flex: '0 0 280px', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '16px', background: '#fff', maxHeight: '80vh', overflowY: 'auto' },
  alertError: { background: '#fee2e2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: '10px' },
  alertSuccess: { background: '#dcfce7', border: '1px solid #bbf7d0', color: '#15803d', padding: '10px 14px', borderRadius: '10px' },
  statusLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, fontWeight: 600, color: '#0f172a' },
  statusValue: { fontSize: 13, fontWeight: 700 },
  statusSecondary: { color: '#b45309' },
  readonlyBox: { border: '1px dashed #cbd5f5', borderRadius: 10, padding: '8px 10px', background: '#f1f5f9', fontWeight: 600, textAlign: 'right', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'auto' },
  readonlyTag: { marginTop: 6, padding: '6px 10px', borderRadius: 10, background: '#e2e8f0', border: '1px solid #cbd5f5', fontSize: 13, fontWeight: 600, color: '#0f172a' },
};

const numericInputStyle = {
  ...styles.input,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const monthName = (m) => MONTH_NAMES[Math.max(1, Math.min(12, Number(m || 0))) - 1] || 'Месяц';
const makeLocalId = () => `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const toInputDate = (value) => (value ? String(value).slice(0, 10) : '');
const endOfMonthIso = (year, month) => {
  const safeYear = Number(year || 0) || new Date().getFullYear();
  const safeMonth = Math.max(1, Math.min(12, Number(month || 1))); // 1-12
  const date = new Date(safeYear, safeMonth, 0); // day 0 -> last day of requested month
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};
const toSqlDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};
const toSqlDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const localMs = d.getTime() - d.getTimezoneOffset() * 60000;
  return new Date(localMs).toISOString().slice(0, 19).replace('T', ' ');
};

const toStr = (value) => (value === 0 || value ? String(value) : '');
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};
const roundMoney = (value) => Math.round(value * 100) / 100;
const formatMoney = (value) => `${roundMoney(Number(value) || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`;
const calcDebt = (row = {}) => Math.max(0, toNumber(row.amountCustomer) - toNumber(row.paidAmount));
const calcUnsigned = (row = {}) => Math.max(0, toNumber(row.amountCustomer) - toNumber(row.signedAmount));
const amountColor = (rowAmount, customerAmount) => {
  const amount = toNumber(rowAmount);
  const customer = toNumber(customerAmount);
  if (amount < customer) return '#b91c1c';
  if (amount > customer) return '#c2410c';
  return '#15803d';
};
const amountBackground = (tone) => {
  switch (tone) {
    case '#b91c1c':
      return '#fef2f2';
    case '#c2410c':
      return '#fff7ed';
    case '#15803d':
      return '#ecfdf5';
    default:
      return '#f8fafc';
  }
};
const normalizeNumericInput = (value = '') => value.replace(/\s+/g, '').replace(/,/g, '.');
const isNumericInputValue = (value = '') => /^-?\d*(?:\.\d*)?$/.test(value);
const formatNumberInputValue = (value) => {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (!raw.trim()) return '';
  const normalized = raw.replace(/\s+/g, '');
  const isNegative = normalized.startsWith('-');
  const unsigned = isNegative ? normalized.slice(1) : normalized;
  if (!unsigned) return isNegative ? '-' : '';

  const hasFraction = unsigned.includes('.');
  const [integerPartRaw = '0', fractionPartRaw = ''] = hasFraction ? unsigned.split('.') : [unsigned, ''];
  const integerNumber = Number(integerPartRaw || '0');
  const formatInteger = () => {
    if (!Number.isFinite(integerNumber)) return integerPartRaw;
    return Math.abs(Math.trunc(integerNumber)).toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const sign = isNegative ? '-' : '';
  if (hasFraction) {
    const intPart = formatInteger();
    const fractionPart = fractionPartRaw.replace(/[^\d]/g, '');
    return `${sign}${intPart},${fractionPart}`;
  }

  if (/^\d+$/.test(unsigned)) {
    return `${sign}${Number(unsigned).toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  }

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) {
    return raw.replace('.', ',');
  }

  return `${sign}${Math.abs(numeric).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};
const formatNumberDisplay = (value, fractionDigits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return value === undefined || value === null ? '' : String(value);
  return num.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
};
function rowHasContent(row = {}) {
  const textFields = ['basis', 'invoiceDate', 'actDate', 'paidAt', 'fromCompany', 'customer', 'objectName'];
  if (textFields.some((key) => (row[key] || '').toString().trim())) return true;
  const amountFields = ['amountCustomer', 'paidAmount', 'signedAmount', 'objectCode'];
  if (amountFields.some((key) => Math.abs(toNumber(row[key])) > 0)) return true;
  return !!row.documentsSent;
}

function normalizeRow(row = {}, order = 0) {
  const cardIdRaw = row.cardId ?? row.card_id ?? row.cardID ?? row.cardid;
  const objectCodeRaw = row.objectCode ?? row.object_code ?? row.objectCodeFull ?? row.objectcode;
  const objectNameRaw = row.objectName ?? row.object_name;
  const basisRaw = row.basis ?? row.reason;
  const amountCustomerRaw = row.amountCustomer ?? row.amount_customer;
  const invoiceDateRaw = row.invoiceDate ?? row.invoice_date;
  const actDateRaw = row.actDate ?? row.act_date;
  const fromCompanyRaw = row.fromCompany ?? row.from_company;
  const paidAmountRaw = row.paidAmount ?? row.paid_amount;
  const paidAtRaw = row.paidAt ?? row.paid_at;
  const signedAmountRaw = row.signedAmount ?? row.signed_amount;
  const documentsSentRaw = row.documentsSent ?? row.documents_sent;
  const documentsSentAtRaw = row.documentsSentAt ?? row.documents_sent_at;
  const rowOrderRaw = row.rowOrder ?? row.row_order;
  const rowOrderNumber = rowOrderRaw !== undefined && rowOrderRaw !== null ? Number(rowOrderRaw) : undefined;

  return {
    id: row.id || null,
    cardId: cardIdRaw ? String(cardIdRaw) : '',
    objectCode: objectCodeRaw ?? '',
    objectName: objectNameRaw || '',
    customer: row.customer || '',
    basis: basisRaw || '',
    amountCustomer: toStr(amountCustomerRaw),
    invoiceDate: toInputDate(invoiceDateRaw),
    actDate: toInputDate(actDateRaw),
    fromCompany: fromCompanyRaw || '',
    paidAmount: toStr(paidAmountRaw),
    paidAt: toInputDate(paidAtRaw),
    signedAmount: toStr(signedAmountRaw),
    documentsSent: documentsSentRaw === true || documentsSentRaw === 1,
    documentsSentAt: documentsSentAtRaw || null,
    rowOrder: Number.isFinite(rowOrderNumber) ? rowOrderNumber : order,
    _localId: row._localId || makeLocalId(),
  };
}

function calcContractTotalWithTmc(card) {
  if (!card) return 0;
  const sheets = card.sheets || {};
  const summary = sheets.summary || {};
  const manual = Array.isArray(summary.contractRows) ? summary.contractRows : [];
  const manualSum = manual.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
  const base = manualSum > 0 ? manualSum : Number(summary.contractTotal) || 0;
  const tmcRows = Array.isArray(sheets.tmc) ? sheets.tmc : [];
  const tmcSale = tmcRows.reduce((acc, row) => acc + (row?.for_sale ? (Number(row.sale_sum) || 0) : 0), 0);
  return Math.round((base + tmcSale) * 100) / 100;
}

function extractPlanAmount(card) {
  const total = calcContractTotalWithTmc(card);
  return total > 0 ? String(total) : '';
}

function getContractTotalValue(card) {
  if (!card) return 0;
  const raw = extractPlanAmount(card);
  return toNumber(raw);
}

function deriveObjectCode(card) {
  if (!card) return '';
  const direct = card.objectCode;
  if (direct !== undefined && direct !== null && direct !== '') {
    const parsed = Number(direct);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (card.objectCodeFull) {
    const match = String(card.objectCodeFull).match(/\d+/);
    if (match) {
      const parsed = Number(match[0]);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  const fallback = Number(card.id);
  return Number.isNaN(fallback) ? '' : fallback;
}

const formatCardLabel = (card) => {
  if (!card) return '';
  const code = card.objectCodeFull || card.objectCode || deriveObjectCode(card) || card.id;
  const name = card.objectName || '';
  const parts = [code, name].filter(Boolean);
  return parts.length ? parts.join(' · ') : `Объект ${card.id}`;
};

const normalizeSearchText = (value = '') => {
  if (value === null || value === undefined) return '';
  return String(value)
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-z0-9а-я]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const createColumnFilters = () => ({
  number: '',
  object: '',
  customer: '',
  basis: '',
  contract: '',
  client: '',
  paid: '',
  signed: '',
  paidAt: '',
  documentsSent: '',
  paidState: '',
});

const enrichRowFromCard = (row, card) => {
  if (!card) return row;
  let changed = false;
  let customer = row.customer;
  let fromCompany = row.fromCompany;
  if (!customer) {
    customer = card.customer || card.company || '';
    if (customer !== row.customer) changed = true;
  }
  if (!fromCompany) {
    fromCompany = card.company || '';
    if (fromCompany !== row.fromCompany) changed = true;
  }
  return changed ? { ...row, customer, fromCompany } : row;
};

const AutoGrowTextarea = React.forwardRef(({ value, onChange, minRows = 2, style = {}, ...props }, forwardedRef) => {
  const innerRef = useRef(null);
  const assignRef = useCallback((node) => {
    innerRef.current = node;
    if (!forwardedRef) return;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else {
      forwardedRef.current = node;
    }
  }, [forwardedRef]);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const base = Math.max(1, minRows) * 20;
    el.style.height = `${Math.max(base, el.scrollHeight)}px`;
  }, [value, minRows]);

  return (
    <textarea
      {...props}
      ref={assignRef}
      value={value}
      onChange={onChange}
      style={{ ...styles.textarea, resize: 'none', minHeight: minRows * 20, ...style }}
    />
  );
});

export default function ClosingDocsPage() {
  const now = new Date();
  const previousPeriod = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [month, setMonth] = useState(previousPeriod.getMonth() + 1);
  const [year, setYear] = useState(previousPeriod.getFullYear());
  const [rows, setRows] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [columnFilters, setColumnFilters] = useState(() => createColumnFilters());
  const [cardToAdd, setCardToAdd] = useState('');
  const [cardInputDrafts, setCardInputDrafts] = useState({});
  const [dirty, setDirty] = useState(false);
  const [cardsRefreshing, setCardsRefreshing] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const autoSaveEnabled = true;
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const autoSaveTimer = useRef(null);
  const handleQuickAdd = () => {
    const card = cards.find((c) => String(c.id) === String(cardToAdd));
    if (!card) return;
    addRowFromCard(card);
    setCardToAdd('');
  };

  const loadRowsAndCards = useCallback(async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const resp = await fetch(apiUrl(`/api/closing-docs?month=${month}&year=${year}`));
      if (!resp.ok) throw new Error('failed');
      const data = await resp.json();
      const fetchedRows = Array.isArray(data.rows) ? data.rows : [];
      const fetchedCards = Array.isArray(data.cards) ? data.cards : [];
      const cardMap = new Map(fetchedCards.map((card) => [String(card.id), card]));
      const normalizedRows = fetchedRows.map((row, idx) => {
        const normalized = normalizeRow(row, idx);
        const card = cardMap.get(String(normalized.cardId || ''));
        return card ? enrichRowFromCard(normalized, card) : normalized;
      });
      setRows(normalizedRows);
      setCards(fetchedCards);
      setDirty(false);
    } catch (e) {
      console.error('load closing docs failed', e);
      setError('Не удалось загрузить реестр. Попробуйте обновить страницу.');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { loadRowsAndCards(); }, [loadRowsAndCards]);

  const shiftMonth = useCallback((delta) => {
    const ref = new Date(year, month - 1 + delta, 1);
    setMonth(ref.getMonth() + 1);
    setYear(ref.getFullYear());
  }, [month, year]);

  const refreshCards = useCallback(async () => {
    if (loading || cardsRefreshing) return;
    setCardsRefreshing(true);
    setError('');
    try {
      const resp = await fetch(apiUrl(`/api/closing-docs?month=${month}&year=${year}`));
      if (!resp.ok) throw new Error('failed');
      const data = await resp.json();
      const fetchedCards = Array.isArray(data.cards) ? data.cards : [];
      setCards(fetchedCards);
    } catch (e) {
      console.error('refresh cards failed', e);
      setError('Не удалось обновить список карточек.');
    } finally {
      setCardsRefreshing(false);
    }
  }, [month, year, loading, cardsRefreshing]);

  useEffect(() => {
    if (!cards.length) return;
    setRows((prev) => {
      let changed = false;
      const map = new Map(cards.map((card) => [String(card.id), card]));
      const next = prev.map((row) => {
        if (row.customer && row.fromCompany) return row;
        const card = map.get(String(row.cardId || ''));
        if (!card) return row;
        const enriched = enrichRowFromCard(row, card);
        if (enriched !== row) changed = true;
        return enriched;
      });
      return changed ? next : prev;
    });
  }, [cards]);

  const updateColumnFilter = useCallback((key, value) => {
    setColumnFilters((prev) => {
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setColumnFilters(createColumnFilters());
  }, []);

  const cardsById = useMemo(() => new Map(cards.map((card) => [String(card.id), card])), [cards]);
  const cardsByCode = useMemo(() => {
    const map = new Map();
    cards.forEach((card) => {
      const derived = deriveObjectCode(card);
      if (derived !== '' && derived !== null && !Number.isNaN(Number(derived))) {
        map.set(String(derived), card);
      }
      if (card.objectCodeFull) {
        const digits = String(card.objectCodeFull).replace(/\D+/g, '');
        if (digits) {
          map.set(digits, card);
        }
      }
    });
    return map;
  }, [cards]);
  const cardsByName = useMemo(() => {
    const map = new Map();
    cards.forEach((card) => {
      const key = normalizeSearchText(card.objectName || card.objectCodeFull || '');
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(card);
    });
    return map;
  }, [cards]);
  const cardsByCustomerName = useMemo(() => {
    const map = new Map();
    cards.forEach((card) => {
      const key = normalizeSearchText(card.customer || card.company || '');
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(card);
    });
    return map;
  }, [cards]);
  const cardOptions = useMemo(() => cards.map((card) => ({
    id: String(card.id),
    label: formatCardLabel(card),
    card,
  })), [cards]);
  const cardLabelById = useMemo(() => {
    const map = new Map();
    cardOptions.forEach((option) => {
      map.set(option.id, option.label);
    });
    return map;
  }, [cardOptions]);

  const rowCardIds = useMemo(() => new Set(rows.map((row) => String(row.cardId || ''))), [rows]);
  const coverage = useMemo(() => cards.map((card) => ({ card, hasRow: rowCardIds.has(String(card.id)) })), [cards, rowCardIds]);
  const missingCards = useMemo(() => coverage.filter((entry) => !entry.hasRow).map((entry) => entry.card), [coverage]);
  const { cardTotalsById, cardsContractTotal } = useMemo(() => {
    const map = new Map();
    let sum = 0;
    cards.forEach((card) => {
      const value = roundMoney(getContractTotalValue(card));
      map.set(String(card.id), value);
      sum += value;
    });
    return { cardTotalsById: map, cardsContractTotal: roundMoney(sum) };
  }, [cards]);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(columnFilters).some(([key, value]) => {
      if (key === 'documentsSent') {
        return Boolean(value);
      }
      if (typeof value === 'string') {
        return Boolean(value.trim());
      }
      return Boolean(value);
    });
  }, [columnFilters]);

  const filteredRows = useMemo(() => {
    const normalized = {
      number: columnFilters.number.trim(),
      object: columnFilters.object.trim().toLowerCase(),
      customer: columnFilters.customer.trim().toLowerCase(),
      basis: columnFilters.basis.trim().toLowerCase(),
      contract: columnFilters.contract.trim().replace(/\s+/g, ''),
      client: columnFilters.client.trim().replace(/\s+/g, ''),
      paid: columnFilters.paid.trim().replace(/\s+/g, ''),
      signed: columnFilters.signed.trim().replace(/\s+/g, ''),
      paidAt: columnFilters.paidAt.trim().toLowerCase(),
      documentsSent: columnFilters.documentsSent,
      paidState: columnFilters.paidState,
    };

    const includesNumeric = (needle, raw) => {
      if (!needle) return true;
      const normalizedNeedle = needle.replace(',', '.');
      const normalizedValue = String(raw ?? '')
        .replace(/\s+/g, '')
        .replace(',', '.');
      if (!normalizedValue) return false;
      return normalizedValue.includes(normalizedNeedle);
    };

    return rows.filter((row, idx) => {
      const rowNumber = idx + 1;
      if (normalized.number && !String(rowNumber).includes(normalized.number)) return false;

      if (normalized.object) {
        const haystack = [
          row.objectName,
          row.objectCode,
          row.cardId ? cardLabelById.get(String(row.cardId)) : '',
        ].map((value) => String(value || '').toLowerCase());
        if (!haystack.some((value) => value.includes(normalized.object))) return false;
      }

      if (normalized.customer && !String(row.customer || '').toLowerCase().includes(normalized.customer)) {
        return false;
      }

      if (normalized.basis && !String(row.basis || '').toLowerCase().includes(normalized.basis)) {
        return false;
      }

      const contractValue = cardTotalsById.get(String(row.cardId));
      if (!includesNumeric(normalized.contract, contractValue)) return false;
      if (!includesNumeric(normalized.client, row.amountCustomer)) return false;
      if (!includesNumeric(normalized.paid, row.paidAmount)) return false;
      if (!includesNumeric(normalized.signed, row.signedAmount)) return false;

      if (normalized.paidAt) {
        const paidAtString = String(row.paidAt || '').toLowerCase();
        if (!paidAtString.includes(normalized.paidAt)) return false;
      }

      if (normalized.documentsSent === 'sent' && !row.documentsSent) return false;
      if (normalized.documentsSent === 'not-sent' && row.documentsSent) return false;

      if (normalized.paidState === 'paid') {
        const amount = toNumber(row.amountCustomer);
        const paidAmount = toNumber(row.paidAmount);
        if (!(amount > 0 && paidAmount + 0.0001 >= amount)) return false;
      }
      if (normalized.paidState === 'unpaid') {
        const amount = toNumber(row.amountCustomer);
        const paidAmount = toNumber(row.paidAmount);
        if (!(amount > 0 && paidAmount + 0.0001 < amount)) return false;
      }

      return true;
    });
  }, [rows, columnFilters, cardLabelById, cardTotalsById]);

  const totals = useMemo(() => {
    const aggregated = rows.reduce((acc, row) => {
      const amount = toNumber(row.amountCustomer);
      const paid = toNumber(row.paidAmount);
      acc.invoiced += amount;
      acc.paid += paid;
      acc.debt += calcDebt(row);
      acc.unsigned += calcUnsigned(row);
      return acc;
    }, { invoiced: 0, paid: 0, debt: 0, unsigned: 0 });
    return {
      invoiced: roundMoney(aggregated.invoiced),
      paid: roundMoney(aggregated.paid),
      debt: roundMoney(aggregated.debt),
      unsigned: roundMoney(aggregated.unsigned),
      contract: cardsContractTotal,
    };
  }, [rows, cardsContractTotal]);

  const filteredTotals = useMemo(() => {
    const aggregated = filteredRows.reduce((acc, row) => {
      const amount = toNumber(row.amountCustomer);
      const paid = toNumber(row.paidAmount);
      acc.invoiced += amount;
      acc.paid += paid;
      acc.debt += calcDebt(row);
      acc.unsigned += calcUnsigned(row);
      return acc;
    }, { invoiced: 0, paid: 0, debt: 0, unsigned: 0 });
    const uniqueCardIds = new Set(filteredRows.map((row) => String(row.cardId || '')));
    let contractSum = 0;
    uniqueCardIds.forEach((cardId) => {
      if (!cardId) return;
      const value = cardTotalsById.get(cardId);
      if (value !== undefined && value !== null) {
        contractSum += value;
      }
    });
    return {
      invoiced: roundMoney(aggregated.invoiced),
      paid: roundMoney(aggregated.paid),
      debt: roundMoney(aggregated.debt),
      unsigned: roundMoney(aggregated.unsigned),
      contract: roundMoney(contractSum),
    };
  }, [filteredRows, cardTotalsById]);

  const addRowFromCard = (card) => {
    if (!card) return;
    const basis = `Основание: ${card.objectName || 'Объект'} — ${monthName(month)} ${year}`;
    const defaultDate = endOfMonthIso(year, month);
    setRows((prev) => ([
      ...prev,
      normalizeRow({
        cardId: card.id,
        objectCode: deriveObjectCode(card),
        objectName: card.objectName || card.objectCodeFull || `Объект ${card.id}`,
        customer: card.customer || '',
        basis,
        amountCustomer: extractPlanAmount(card),
        invoiceDate: defaultDate,
        actDate: defaultDate,
        fromCompany: card.company || '',
      }, prev.length),
    ]));
    setDirty(true);
  };

  const addMissingRows = () => {
    if (!missingCards.length) return;
    setRows((prev) => {
      const base = prev.length;
      const additions = missingCards.map((card, idx) => normalizeRow({
        cardId: card.id,
        objectCode: deriveObjectCode(card),
        objectName: card.objectName || card.objectCodeFull || `Объект ${card.id}`,
        customer: card.customer || '',
        basis: `Основание: ${card.objectName || 'Объект'} — ${monthName(month)} ${year}`,
        amountCustomer: extractPlanAmount(card),
        invoiceDate: endOfMonthIso(year, month),
        actDate: endOfMonthIso(year, month),
        fromCompany: card.company || '',
      }, base + idx));
      return [...prev, ...additions];
    });
    setDirty(true);
  };

  const addEmptyRow = () => {
    const defaultDate = endOfMonthIso(year, month);
    setRows((prev) => ([
      ...prev,
      normalizeRow({
        invoiceDate: defaultDate,
        actDate: defaultDate,
      }, prev.length),
    ]));
    setDirty(true);
  };

  const mergeRow = useCallback((index, changes) => {
    setRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, ...changes } : row)));
    setDirty(true);
  }, [setDirty, setRows]);

  const onCardChange = useCallback((index, cardId) => {
    const card = cards.find((c) => String(c.id) === String(cardId));
    if (!card) {
      mergeRow(index, { cardId: '', objectCode: '', objectName: '', customer: '', fromCompany: '' });
      return;
    }
    const currentRow = rows[index];
    const next = {
      cardId: String(card.id),
      objectCode: deriveObjectCode(card),
      objectName: card.objectName || card.objectCodeFull || `Объект ${card.id}`,
      fromCompany: card.company || '',
    };
    if (!currentRow?.customer?.trim()) {
      next.customer = card.customer || card.company || '';
    }
    mergeRow(index, next);
  }, [cards, mergeRow, rows]);

  const handleCardLookupInput = useCallback((index, localId, rawValue) => {
    const value = rawValue || '';
    setCardInputDrafts((prev) => ({ ...prev, [localId]: value }));
    const trimmed = value.trim();
    if (!trimmed) {
      setCardInputDrafts((prev) => {
        const next = { ...prev };
        delete next[localId];
        return next;
      });
      onCardChange(index, '');
      return;
    }
    const lower = trimmed.toLowerCase();
    const exactOption = cardOptions.find((option) => option.label.toLowerCase() === lower);
    const numericCandidate = trimmed.replace(/\s+/g, '');
    let matchedCard = exactOption?.card || null;
    if (!matchedCard && /^\d+$/.test(numericCandidate)) {
      matchedCard = cardsById.get(numericCandidate) || cardsByCode.get(numericCandidate) || null;
    }
    if (!matchedCard) {
      matchedCard = cards.find((card) => formatCardLabel(card).toLowerCase().includes(lower)) || null;
    }
    if (matchedCard) {
      setCardInputDrafts((prev) => {
        const next = { ...prev };
        delete next[localId];
        return next;
      });
      onCardChange(index, matchedCard.id);
    }
  }, [cardOptions, cardsById, cardsByCode, cards, onCardChange]);

  const deleteRow = (index) => {
    if (!window.confirm('Удалить строку реестра?')) return;
    setRows((prev) => prev.filter((_, idx) => idx !== index));
    setDirty(true);
  };

  const duplicateRow = (index) => {
    let created = false;
    setRows((prev) => {
      const row = prev[index];
      if (!row) return prev;
      const clone = normalizeRow({
        ...row,
        id: null,
        documentsSent: false,
        documentsSentAt: null,
        paidAmount: '',
        paidAt: '',
        signedAmount: '',
      }, prev.length);
      created = true;
      return [...prev, clone];
    });
    if (created) setDirty(true);
  };

  const toggleSent = (index) => {
    const row = rows[index];
    if (!row) return;
    const next = !row.documentsSent;
    mergeRow(index, { documentsSent: next, documentsSentAt: next ? new Date().toISOString() : null });
  };

  const handleNumberInput = useCallback((index, key, rawValue) => {
    const normalized = normalizeNumericInput(String(rawValue || ''));
    if (normalized === '' || isNumericInputValue(normalized)) {
      mergeRow(index, { [key]: normalized });
    }
  }, [mergeRow]);

  const sanitizeRows = useCallback((source = rows) => (source || rows).map((row, idx) => {
    const { _localId, missingCard, missingCardInfo, ...rest } = row;
    const cardIdNum = row.cardId ? Number(row.cardId) : null;
    return {
      ...rest,
      cardId: cardIdNum,
      objectCode: Number(row.objectCode) || 0,
      rowOrder: idx,
      amountCustomer: row.amountCustomer === '' ? 0 : Number(row.amountCustomer),
      paidAmount: row.paidAmount === '' ? 0 : Number(row.paidAmount),
      signedAmount: row.signedAmount === '' ? 0 : Number(row.signedAmount),
      invoiceDate: toSqlDate(row.invoiceDate),
      actDate: toSqlDate(row.actDate),
      paidAt: toSqlDate(row.paidAt),
      documentsSentAt: toSqlDateTime(row.documentsSentAt),
    };
  }), [rows]);

  const saveRows = useCallback(async ({ auto = false } = {}) => {
    const prunedRows = rows.filter((row) => row.cardId || rowHasContent(row));
    if (prunedRows.length !== rows.length) {
      setRows(prunedRows);
      setDirty(true);
    }

    const missingCard = prunedRows
      .map((row, idx) => ({ idx, hasCard: Boolean(row.cardId) }))
      .filter((entry) => !entry.hasCard);
    if (missingCard.length) {
      if (missingCard.length === 1) {
        setError(`В строке №${missingCard[0].idx + 1} не выбран объект. Если строка скрыта фильтром, очистите его.`);
      } else {
        const preview = missingCard.slice(0, 3).map((entry) => `№${entry.idx + 1}`).join(', ');
        const suffix = missingCard.length > 3 ? '…' : '';
        setError(`В строках ${preview}${suffix} не выбраны объекты. Если строка скрыта фильтром, очистите его.`);
      }
      return false;
    }

    const invalidCard = prunedRows
      .map((row, idx) => ({ idx, parsed: row.cardId ? Number(row.cardId) : null }))
      .filter((entry) => entry.parsed !== null && (!Number.isFinite(entry.parsed) || entry.parsed <= 0));
    if (invalidCard.length) {
      const first = invalidCard[0];
      setError(`В строке №${first.idx + 1} выбран некорректный объект. Выберите объект заново.`);
      return false;
    }

    const workingRows = prunedRows;
    if (auto) {
      setAutoSaving(true);
    } else {
      setSaving(true);
      setInfo('');
    }
    setError('');
    try {
      const payload = { month, year, rows: sanitizeRows(workingRows) };
      const resp = await fetch(apiUrl('/api/closing-docs'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const parseBackendError = async () => {
          try {
            const data = await resp.json();
            if (typeof data === 'string') return data;
            if (data && typeof data === 'object') {
              return data.details || data.error || JSON.stringify(data);
            }
          } catch (_) {
            try {
              const text = await resp.text();
              if (text) return text;
            } catch (_) {}
          }
          return '';
        };
        const details = (await parseBackendError()) || `HTTP_${resp.status}`;
        const err = new Error(details);
        err.httpStatus = resp.status;
        throw err;
      }

      if (auto) {
        setDirty(false);
        setLastAutoSavedAt(new Date());
      } else {
        setInfo('Реестр сохранён.');
        setDirty(false);
        await loadRowsAndCards();
      }
      return true;
    } catch (e) {
      console.error('save closing docs failed', e);
      const base = auto ? 'Автосохранение не удалось.' : 'Не удалось сохранить изменения.';
      const hint = e?.message && e.message !== 'save_failed' ? ` ${e.message}` : '';
      setError(`${base}${hint}`.trim());
      return false;
    } finally {
      if (auto) {
        setAutoSaving(false);
      } else {
        setSaving(false);
      }
    }
  }, [rows, month, year, loadRowsAndCards, sanitizeRows]);

  const copyPreviousMonth = async () => {
    if (rows.length && !window.confirm('Заменить текущие строки значениями за прошлый месяц?')) return;
    const ref = new Date(year, month - 2, 1);
    const prevMonth = ref.getMonth() + 1;
    const prevYear = ref.getFullYear();
    setCopying(true);
    setError('');
    setInfo('');
    try {
      const resp = await fetch(apiUrl(`/api/closing-docs/month/${prevYear}/${prevMonth}/copy`));
      if (!resp.ok) throw new Error('copy_failed');
      const data = await resp.json();
      const srcRows = Array.isArray(data.rows) ? data.rows : [];
      const defaultDate = endOfMonthIso(year, month);
      const mapped = srcRows.map((row, idx) => normalizeRow({
        ...row,
        id: null,
        invoiceDate: defaultDate,
        actDate: defaultDate,
        paidAmount: '',
        paidAt: '',
        signedAmount: '',
        documentsSent: false,
        documentsSentAt: null,
      }, idx));
      const missingMatches = [];
      const resolved = mapped.map((row) => {
        const rowId = row.cardId ? String(row.cardId) : '';
        const valueMatches = [];
        if (row.objectCode !== undefined && row.objectCode !== null && row.objectCode !== '') {
          valueMatches.push(String(row.objectCode));
        }
        const addNumericHints = (text) => {
          if (!text) return;
          const matches = String(text).match(/\d{3,}/g);
          if (!matches) return;
          matches.forEach((match) => valueMatches.push(match));
        };
        addNumericHints(row.objectName);
        addNumericHints(row.basis);
        addNumericHints(row.customer);

        const tryCodeMatch = () => {
          for (const hint of valueMatches) {
            const compact = hint.replace(/^0+/, '');
            const variants = [hint];
            if (compact && compact !== hint) variants.push(compact);
            const numeric = Number(hint);
            if (Number.isFinite(numeric)) {
              variants.push(String(numeric));
            }
            for (const variant of variants) {
              if (!variant) continue;
              const candidate = cardsByCode.get(variant);
              if (candidate) return candidate;
            }
          }
          return null;
        };

        const findCard = () => {
          if (rowId) {
            const byId = cardsById.get(rowId);
            if (byId) return byId;
          }
          const byCode = tryCodeMatch();
          if (byCode) return byCode;
          const nameKey = normalizeSearchText(row.objectName || '');
          if (nameKey) {
            const nameMatches = cardsByName.get(nameKey);
            if (nameMatches && nameMatches.length) {
              if (nameMatches.length === 1) return nameMatches[0];
              const customerKey = normalizeSearchText(row.customer || '');
              if (customerKey) {
                const precise = nameMatches.find((card) => normalizeSearchText(card.customer || card.company || '') === customerKey);
                if (precise) return precise;
              }
            }
          }
          const customerKey = normalizeSearchText(row.customer || '');
          if (customerKey) {
            const customerMatches = cardsByCustomerName.get(customerKey);
            if (customerMatches && customerMatches.length === 1) return customerMatches[0];
          }
          return null;
        };

        const card = findCard();
        if (!card) {
          const descriptor = [row.objectCode || rowId || '', row.objectName || '', row.customer || '']
            .map((part) => String(part || '').trim())
            .filter(Boolean)
            .join(' · ') || 'Объект без названия';
          missingMatches.push(descriptor);
          return {
            ...row,
            cardId: rowId || row.cardId || '',
            missingCard: true,
            missingCardInfo: descriptor,
          };
        }
        return enrichRowFromCard({
          ...row,
          cardId: String(card.id),
          objectCode: deriveObjectCode(card),
          objectName: card.objectName || card.objectCodeFull || `Объект ${card.id}`,
          missingCard: false,
          missingCardInfo: null,
        }, card);
      });
      setRows(resolved);
      setCardInputDrafts({});
      setDirty(true);
      if (missingMatches.length) {
        const preview = missingMatches.slice(0, 4).join('; ');
        const suffix = missingMatches.length > 4 ? ` и ещё ${missingMatches.length - 4}` : '';
        setInfo(`Скопировано, но нет актуальных карточек для: ${preview}${suffix}. Строки оставлены, можно сохранить.`);
      } else {
        setInfo('Строки скопированы из предыдущего месяца.');
      }
    } catch (e) {
      console.error('copy previous month failed', e);
      setError('Не удалось получить данные прошлого месяца.');
    } finally {
      setCopying(false);
    }
  };

  useEffect(() => {
    if (!dirty || loading || saving || copying || autoSaving || !autoSaveEnabled) {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
      return;
    }
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveRows({ auto: true });
    }, 1800);
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
    };
  }, [autoSaveEnabled, dirty, loading, saving, copying, autoSaving, saveRows]);

  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    setCardInputDrafts((prev) => {
      const allowed = new Set(rows.map((row) => row._localId));
      let changed = false;
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (allowed.has(key)) {
          next[key] = prev[key];
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [rows]);

  const filterHeaderCellStyle = {
    ...styles.th,
    background: '#fff',
    padding: '6px 8px',
    borderTop: '1px solid #e5e7eb',
  };

  const lastAutoLabel = lastAutoSavedAt ? lastAutoSavedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Реестр закрывающих документов</h1>
          <p style={{ margin: '4px 0', color: '#6b7280' }}>Отслеживайте счета, акты и отправку документов по месяцам.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 220 }}>
          <button
            style={{
              ...styles.button,
              background: dirty ? '#047857' : '#1d4ed8',
              cursor: (saving || loading || copying || !rows.length) ? 'not-allowed' : 'pointer',
            }}
            disabled={saving || loading || copying || !rows.length}
            onClick={() => saveRows()}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
          <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'right', maxWidth: 260 }}>
            {autoSaving
              ? 'Автосохранение…'
              : lastAutoLabel
                ? `Автосохранено в ${lastAutoLabel}`
                : 'Автосохранение включено постоянно. Кнопка сохраняет изменения принудительно.'}
          </div>
        </div>
      </div>

      <div style={styles.toolbar}>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span>Месяц</span>
          <button style={styles.secondaryBtn} onClick={() => shiftMonth(-1)}>←</button>
          <select style={styles.select} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((label, idx) => <option key={label} value={idx + 1}>{label}</option>)}
          </select>
          <button style={styles.secondaryBtn} onClick={() => shiftMonth(1)}>→</button>
        </label>
        <label>
          Год
          <select style={styles.select} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {Array.from({ length: 5 }).map((_, idx) => {
              const y = now.getFullYear() - 2 + idx;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </label>
        <button style={styles.secondaryBtn} onClick={refreshCards} disabled={loading || cardsRefreshing}>
          {cardsRefreshing ? 'Обновление объектов…' : 'Обновить объекты'}
        </button>
        <button style={styles.secondaryBtn} onClick={copyPreviousMonth} disabled={copying || loading}>
          {copying ? 'Копирование…' : 'Копировать прошлый месяц'}
        </button>
        <button style={styles.secondaryBtn} onClick={addMissingRows} disabled={!missingCards.length}>
          Добавить все объекты ({missingCards.length})
        </button>
        <button style={styles.secondaryBtn} onClick={addEmptyRow}>
          Пустая строка
        </button>
        <button
          style={{ ...styles.secondaryBtn, borderColor: '#f87171', color: '#b91c1c' }}
          onClick={() => {
            if (!rows.length || window.confirm('Очистить все строки текущего месяца?')) {
              setRows([]);
              setDirty(true);
              setInfo('Все строки очищены.');
            }
          }}
        >
          Очистить все
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select style={{ ...styles.select, minWidth: 220 }} value={cardToAdd} onChange={(e) => setCardToAdd(e.target.value)}>
            <option value="">Добавить по объекту…</option>
            {cards.map((card) => (
              <option key={card.id} value={card.id}>
                {[card.objectCode || card.objectCodeFull || card.id, card.objectName].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
          <button style={styles.secondaryBtn} onClick={handleQuickAdd}>Добавить</button>
        </div>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', margin:'10px 0 6px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div style={{ fontWeight:600, color:'#0f172a' }}>Фильтр по отправке документов:</div>
          {[
            { value:'', label:'Все' },
            { value:'sent', label:'Отправлены' },
            { value:'not-sent', label:'Не отправлены' },
          ].map((option) => {
            const active = columnFilters.documentsSent === option.value;
            return (
              <button
                key={option.value || 'all'}
                onClick={() => updateColumnFilter('documentsSent', option.value)}
                style={{
                  ...styles.secondaryBtn,
                  padding:'6px 12px',
                  borderColor: active ? '#1d4ed8' : '#cbd5e1',
                  color: active ? '#0f172a' : '#334155',
                  background: active ? '#e0f2fe' : '#fff',
                  boxShadow: active ? '0 4px 12px rgba(37,99,235,0.15)' : 'none'
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div style={{ fontWeight:600, color:'#0f172a' }}>Фильтр по оплате счетов:</div>
          {[
            { value:'', label:'Все' },
            { value:'paid', label:'Оплаченные' },
            { value:'unpaid', label:'Неоплаченные' },
          ].map((option) => {
            const active = columnFilters.paidState === option.value;
            return (
              <button
                key={option.value || 'all-paid'}
                onClick={() => updateColumnFilter('paidState', option.value)}
                style={{
                  ...styles.secondaryBtn,
                  padding:'6px 12px',
                  borderColor: active ? '#047857' : '#cbd5e1',
                  color: active ? '#065f46' : '#334155',
                  background: active ? '#dcfce7' : '#fff',
                  boxShadow: active ? '0 4px 12px rgba(4,120,87,0.15)' : 'none'
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={styles.summaryGrid}>
        {(() => {
          const source = hasActiveFilters
            ? [
                { key: 'contract', label: 'Сумма контрактов', value: filteredTotals.contract, accent: '#0f172a' },
                { key: 'invoiced', label: 'Выставлено счетов', value: filteredTotals.invoiced, accent: '#1d4ed8' },
                { key: 'paid', label: 'Оплачено клиентом', value: filteredTotals.paid, accent: '#047857' },
                { key: 'debt', label: 'Дебиторская задолженность', value: filteredTotals.debt, accent: '#b91c1c' },
                { key: 'unsigned', label: 'Неподписанные акты', value: filteredTotals.unsigned, accent: '#a16207' },
              ]
            : [
                { key: 'contract', label: 'Сумма контрактов', value: totals.contract, accent: '#0f172a' },
                { key: 'invoiced', label: 'Выставлено счетов', value: totals.invoiced, accent: '#1d4ed8' },
                { key: 'paid', label: 'Оплачено клиентом', value: totals.paid, accent: '#047857' },
                { key: 'debt', label: 'Дебиторская задолженность', value: totals.debt, accent: '#b91c1c' },
                { key: 'unsigned', label: 'Неподписанные акты', value: totals.unsigned, accent: '#a16207' },
              ];
          return source.map((item) => (
              <div key={item.key} style={{ ...styles.summaryCard, borderColor: `${item.accent}33` }}>
                <div style={{ ...styles.summaryLabel, color: item.accent }}>
                  {item.label}
                  {hasActiveFilters && (
                    <span style={{ marginLeft: 6, fontWeight: 600 }}>(по фильтру)</span>
                  )}
                </div>
                <div style={{ ...styles.summaryValue, color: item.accent }}>{formatMoney(item.value)}</div>
                {hasActiveFilters && (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Показывает только видимые строки</div>
                )}
              </div>
          ));
        })()}
      </div>

      {error && <div style={styles.alertError}>{error}</div>}
      {info && <div style={styles.alertSuccess}>{info}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          style={{
            ...styles.secondaryBtn,
            background: showSidebar ? '#e0e7ff' : '#fff',
            borderColor: showSidebar ? '#6366f1' : '#d1d5db',
            color: showSidebar ? '#312e81' : '#374151',
          }}
          onClick={() => setShowSidebar((prev) => !prev)}
        >
          {showSidebar ? 'Скрыть актуальные расчёты' : 'Показать актуальные расчёты'}
        </button>
      </div>

      <div style={styles.grid}>
        <div style={{ flex: 1 }}>
          <div style={styles.tableWrap}>
            <div style={styles.tableScroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 60 }} rowSpan={2}>№</th>
                  <th style={{ ...styles.th, minWidth: 220 }} rowSpan={2}>Объект</th>
                  <th style={{ ...styles.th, minWidth: 180 }} rowSpan={2}>Заказчик</th>
                  <th style={{ ...styles.th, minWidth: 220 }} rowSpan={2}>Основание</th>
                  <th style={{ ...styles.th, textAlign: 'center' }} colSpan={4}>Суммы</th>
                  <th style={{ ...styles.th, minWidth: 140 }} rowSpan={2}>Дата оплаты</th>
                  <th style={{ ...styles.th, minWidth: 160 }} rowSpan={2}>Документы отправлены</th>
                  <th style={{ ...styles.th, minWidth: 140 }} rowSpan={2}>Действия</th>
                </tr>
                <tr>
                  <th style={{ ...styles.th, minWidth: 150 }}>Контракта</th>
                  <th style={{ ...styles.th, minWidth: 150 }}>Клиента</th>
                  <th style={{ ...styles.th, minWidth: 150 }}>Оплачено</th>
                  <th style={{ ...styles.th, minWidth: 150 }}>Подписано</th>
                </tr>
                <tr>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="№"
                      value={columnFilters.number}
                      onChange={(e) => updateColumnFilter('number', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="Название / код"
                      value={columnFilters.object}
                      onChange={(e) => updateColumnFilter('object', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="Заказчик"
                      value={columnFilters.customer}
                      onChange={(e) => updateColumnFilter('customer', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="Основание"
                      value={columnFilters.basis}
                      onChange={(e) => updateColumnFilter('basis', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="Сумма"
                      value={columnFilters.contract}
                      onChange={(e) => updateColumnFilter('contract', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="Сумма"
                      value={columnFilters.client}
                      onChange={(e) => updateColumnFilter('client', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="Сумма"
                      value={columnFilters.paid}
                      onChange={(e) => updateColumnFilter('paid', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="Сумма"
                      value={columnFilters.signed}
                      onChange={(e) => updateColumnFilter('signed', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <input
                      style={styles.columnFilterInput}
                      placeholder="ГГГГ-ММ-ДД"
                      value={columnFilters.paidAt}
                      onChange={(e) => updateColumnFilter('paidAt', e.target.value)}
                    />
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <select
                      style={styles.columnFilterSelect}
                      value={columnFilters.documentsSent}
                      onChange={(e) => updateColumnFilter('documentsSent', e.target.value)}
                    >
                      <option value="">Все</option>
                      <option value="sent">Отправлены</option>
                      <option value="not-sent">Не отправлены</option>
                    </select>
                  </th>
                  <th style={filterHeaderCellStyle}>
                    <button
                      style={{
                        ...styles.secondaryBtn,
                        width: '100%',
                        opacity: hasActiveFilters ? 1 : 0.6,
                        cursor: hasActiveFilters ? 'pointer' : 'not-allowed'
                      }}
                      onClick={resetFilters}
                      disabled={!hasActiveFilters}
                    >
                      Сбросить
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const absoluteIndex = rows.indexOf(row);
                  const rowNumber = absoluteIndex + 1;
                  const contractValue = cardTotalsById.get(String(row.cardId));
                  const paidTone = amountColor(row.paidAmount, row.amountCustomer);
                  const paidBg = amountBackground(paidTone);
                  const paymentDiff = (toNumber(row.amountCustomer) || 0) - (toNumber(row.paidAmount) || 0);
                  return (
                    <tr key={row._localId}>
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700 }}>
                        <div style={{ fontSize: 18 }}>{rowNumber}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Код: {row.objectCode || '—'}</div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>
                          {row.cardId ? `Карта № ${row.cardId}` : 'Карта не выбрана'}
                        </div>
                        <input
                          type="text"
                          list="closing-docs-card-options"
                          autoComplete="off"
                          placeholder="Введите код или название"
                          style={{ ...styles.input, width: '100%' }}
                          value={cardInputDrafts[row._localId] ?? (row.cardId ? (cardLabelById.get(String(row.cardId)) || '') : (row.objectName || ''))}
                          onChange={(e) => handleCardLookupInput(absoluteIndex, row._localId, e.target.value)}
                        />
                        {row.objectName && (
                          <div style={{ ...styles.readonlyTag, marginTop: 8 }}>{row.objectName}</div>
                        )}
                        {row.missingCard && (
                          <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 10, background: '#fff7ed', border: '1px solid #fcd34d', color: '#b45309', fontSize: 12, fontWeight: 600 }}>
                            Нет актуальной карточки за выбранный месяц. Проверьте объект или актуализируйте расчёт.
                          </div>
                        )}
                        {row.cardId && (
                          <div style={{ marginTop: 6 }}>
                            <a
                              href={`/card-calc/${encodeURIComponent(row.objectCode || row.cardId)}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#2563eb', fontWeight: 600 }}
                            >
                              Открыть карточку объекта
                            </a>
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <AutoGrowTextarea
                          minRows={3}
                          value={row.customer}
                          onChange={(e) => mergeRow(absoluteIndex, { customer: e.target.value })}
                          placeholder="Наименование заказчика"
                        />
                      </td>
                      <td style={styles.td}>
                        <AutoGrowTextarea
                          minRows={3}
                          value={row.basis}
                          onChange={(e) => mergeRow(absoluteIndex, { basis: e.target.value })}
                          placeholder="Опишите основание выставления"
                        />
                      </td>
                      <td style={{ ...styles.td, minWidth: 150 }}>
                        <div style={styles.readonlyBox}>
                          {contractValue !== undefined && contractValue !== null ? formatMoney(contractValue) : '—'}
                        </div>
                      </td>
                      <td style={{ ...styles.td, minWidth: 150 }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={numericInputStyle}
                          value={formatNumberInputValue(row.amountCustomer)}
                          onChange={(e) => handleNumberInput(absoluteIndex, 'amountCustomer', e.target.value)}
                        />
                      </td>
                      <td style={{ ...styles.td, minWidth: 150 }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={{
                            ...numericInputStyle,
                            borderColor: paidTone,
                            color: paidTone,
                            backgroundColor: paidBg,
                          }}
                          value={formatNumberInputValue(row.paidAmount)}
                          onChange={(e) => handleNumberInput(absoluteIndex, 'paidAmount', e.target.value)}
                        />
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                          Разница: {paymentDiff <= 0 ? 'оплачено' : `${formatNumberDisplay(paymentDiff)} ₽`}
                        </div>
                      </td>
                      <td style={{ ...styles.td, minWidth: 150 }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={{
                            ...numericInputStyle,
                            borderColor: amountColor(row.signedAmount, row.amountCustomer),
                            color: amountColor(row.signedAmount, row.amountCustomer),
                          }}
                          value={formatNumberInputValue(row.signedAmount)}
                          onChange={(e) => handleNumberInput(absoluteIndex, 'signedAmount', e.target.value)}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="date"
                          style={styles.input}
                          value={row.paidAt}
                          onChange={(e) => mergeRow(absoluteIndex, { paidAt: e.target.value })}
                        />
                      </td>
                      <td style={styles.td}>
                        <button
                          style={{
                            ...styles.secondaryBtn,
                            width: '100%',
                            background: row.documentsSent ? '#dcfce7' : '#fef9c3',
                            borderColor: row.documentsSent ? '#15803d' : '#f59e0b',
                          }}
                          onClick={() => toggleSent(absoluteIndex)}
                        >
                          {row.documentsSent ? 'Отправлены' : 'Не отправлены'}
                        </button>
                        {row.documentsSent && row.documentsSentAt && (
                          <div style={{ fontSize: 11, marginTop: 4, color: '#059669' }}>
                            {new Date(row.documentsSentAt).toLocaleString('ru-RU')}
                          </div>
                        )}
                      </td>
                      <td style={{ ...styles.td, minWidth: 140 }}>
                        <button style={{ ...styles.secondaryBtn, width: '100%', marginBottom: 6 }} onClick={() => duplicateRow(absoluteIndex)}>Дублировать</button>
                        <button style={{ ...styles.secondaryBtn, width: '100%', borderColor: '#f87171', color: '#b91c1c' }} onClick={() => deleteRow(absoluteIndex)}>Удалить</button>
                      </td>
                    </tr>
                  );
                })}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan={11} style={{ ...styles.td, textAlign: 'center', color: '#94a3b8' }}>
                      Строки не найдены. Добавьте объекты или измените фильтр.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
            <datalist id="closing-docs-card-options">
              {cardOptions.map((option) => (
                <option key={option.id} value={option.label} />
              ))}
            </datalist>
          </div>
        </div>
        {showSidebar && (
          <aside style={styles.sidebar}>
            <h3 style={{ marginTop: 0 }}>Актуальные расчёты</h3>
            <p style={{ marginTop: 0, color: '#6b7280', fontSize: 13 }}>Зелёным отмечены объекты, для которых создана строка реестра. Остальные рекомендуется добавить.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {coverage.map(({ card, hasRow }) => (
                <div key={card.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '8px 10px', background: hasRow ? '#f8fafc' : '#fff7ed' }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{[card.objectCode || card.objectCodeFull || card.id, card.objectName].filter(Boolean).join(' · ')}</div>
                  {card.customer && <div style={{ fontSize: 12, color: '#6b7280' }}>{card.customer}</div>}
                  <div style={{ fontSize: 11, marginTop: 4, color: hasRow ? '#15803d' : '#c2410c' }}>{hasRow ? 'В реестре' : 'Нет в реестре'}</div>
                </div>
              ))}
              {!coverage.length && <div style={{ color: '#94a3b8', fontSize: 13 }}>Нет актуальных карточек за выбранный период.</div>}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
